import api from './api';

class NotificationService {
  /**
   * Get notifications for the current user
   * @param {Object} params - Query parameters (limit, offset, filter options)
   * @returns {Promise<Object>} Paginated notifications response
   */
  async getNotifications(params = {}) {
    try {
      const response = await api.get('/api/notifications/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get distinct filter values (event types, sites, participants, studies) for the
   * current user's notifications, scoped to the selected sponsor.
   * @param {string|number|null} sponsorId
   * @returns {Promise<Object>} { event_types, sites, participants, studies }
   */
  async getFacets(sponsorId = null) {
    try {
      const params = {};
      if (sponsorId) params.sponsor = sponsorId;
      const response = await api.get('/api/notifications/facets/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching notification facets:', error);
      return { event_types: [], sites: [], participants: [], studies: [] };
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise<number>} Unread notification count
   */
  async getUnreadCount(sponsorId = null) {
    try {
      // If there is no auth token, skip calling the API. This avoids
      // unnecessary polling on public pages like login / password reset.
      const hasToken =
        typeof window !== 'undefined' &&
        (localStorage.getItem('authToken') || sessionStorage.getItem('authToken'));

      if (!hasToken) {
        return 0;
      }

      const params = {};
      if (sponsorId) params.sponsor = sponsorId;
      const response = await api.get('/api/notifications/unread_count/', { params });
      return response.data.unread_count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  /**
   * Get recent notifications (last 10), scoped to the selected sponsor.
   * @param {string|number|null} sponsorId
   * @returns {Promise<Array>} Recent notifications
   */
  async getRecentNotifications(sponsorId = null) {
    try {
      const params = {};
      if (sponsorId) params.sponsor = sponsorId;
      const response = await api.get('/api/notifications/recent/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent notifications:', error);
      throw error;
    }
  }

  /**
   * Mark specific notifications as read
   * @param {Array<string>} notificationIds - Array of notification IDs to mark as read
   * @returns {Promise<Object>} Update result
   */
  async markAsRead(notificationIds) {
    try {
      const response = await api.post('/api/notifications/mark_read/', {
        ids: notificationIds
      });
      return response.data;
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  }

  /**
   * Mark specific notifications as unread
   * @param {Array<string>} notificationIds - Array of notification IDs to mark as unread
   * @returns {Promise<Object>} Update result
   */
  async markAsUnread(notificationIds) {
    try {
      const response = await api.post('/api/notifications/mark_unread/', {
        ids: notificationIds
      });
      return response.data;
    } catch (error) {
      console.error('Error marking notifications as unread:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for the current user
   * @returns {Promise<Object>} Update result
   */
  async markAllAsRead(sponsorId = null) {
    try {
      const params = {};
      if (sponsorId) params.sponsor = sponsorId;
      const response = await api.post('/api/notifications/mark_all_read/', null, { params });
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Archive specific notifications
   * @param {Array<string>} notificationIds - Array of notification IDs to archive
   * @returns {Promise<Object>} Archive result
   */
  async archiveNotifications(notificationIds) {
    try {
      const response = await api.post('/api/notifications/archive/', {
        ids: notificationIds
      });
      return response.data;
    } catch (error) {
      console.error('Error archiving notifications:', error);
      throw error;
    }
  }

  /**
   * Get notification event types
   * @returns {Promise<Array>} Available notification event types
   */
  async getEventTypes() {
    try {
      const response = await api.get('/api/notification-types/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching notification event types:', error);
      throw error;
    }
  }

  /**
   * Get user notification subscriptions/preferences
   * @returns {Promise<Array>} User notification subscriptions
   */
  async getSubscriptions() {
    try {
      const response = await api.get('/api/subscriptions/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching notification subscriptions:', error);
      throw error;
    }
  }

  /**
   * Get default subscription preferences for all event types
   * @returns {Promise<Array>} Default subscription preferences
   */
  async getDefaultSubscriptions() {
    try {
      const response = await api.get('/api/subscriptions/defaults/');
      return response.data;
    } catch (error) {
      console.error('Error fetching default subscriptions:', error);
      throw error;
    }
  }

  /**
   * Update notification subscription
   * @param {string} subscriptionId - Subscription ID to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated subscription
   */
  async updateSubscription(subscriptionId, updates) {
    try {
      const response = await api.patch(`/api/subscriptions/${subscriptionId}/`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating notification subscription:', error);
      throw error;
    }
  }

  /**
   * Create new notification subscription
   * @param {Object} subscriptionData - Subscription data
   * @returns {Promise<Object>} Created subscription
   */
  async createSubscription(subscriptionData) {
    try {
      const response = await api.post('/api/subscriptions/', subscriptionData);
      return response.data;
    } catch (error) {
      console.error('Error creating notification subscription:', error);
      throw error;
    }
  }

  /**
   * Delete notification subscription
   * @param {string} subscriptionId - Subscription ID to delete
   * @returns {Promise<void>}
   */
  async deleteSubscription(subscriptionId) {
    try {
      await api.delete(`/api/subscriptions/${subscriptionId}/`);
    } catch (error) {
      console.error('Error deleting notification subscription:', error);
      throw error;
    }
  }
}

export default new NotificationService();
